import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { phoneValidation } from "./forgetPasswordValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import { PhoneInput } from "react-international-phone";
import useOtp from "../register/useOtp";

function ForgetPassword() {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const recaptchaRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(phoneValidation),
    mode: "onBlur",
  });

  const { sendOtpFn } = useOtp();

  const onSubmit = async (formData) => {
    // console.log("form data: ", formData);
    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      // Fixed: Use 'phone' field name to match validation schema
      await sendOtpFn({ data: {}, mobile: formData.phone });
      setSuccessMessage(
        "If an account exists for this phone number, you will receive reset instructions shortly."
      );
    } catch (error) {
      setErrorMessage("Failed to send reset instructions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Simplified phone number formatting
  const formatPhoneNumber = (value, country) => {
    if (!country?.country?.dialCode) return value;

    const dialCode = country.country.dialCode;
    let cleanNumber = value.replace(/\D/g, "");

    // Remove leading zeros after dial code
    if (cleanNumber.startsWith(dialCode + "0")) {
      cleanNumber = dialCode + cleanNumber.slice(dialCode.length + 1);
    }

    // Ensure proper formatting with +
    if (!cleanNumber.startsWith(dialCode)) {
      cleanNumber = dialCode + cleanNumber;
    }

    return "+" + cleanNumber;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#f8fafc] to-[#e0e7ef] py-8 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl px-12 py-8 flex flex-col items-center animate-fade-in">
        <img src="/zstore_logo.png" alt="Z-Store Logo" className="h-16 mb-6" />
        <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
          Forgot your password?
        </h2>
        <p className="text-gray-500 mb-8 text-center text-base">
          Enter your phone number and we'll send you instructions to reset your
          password.
        </p>

        <form
          className="w-full max-w-md flex flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="relative">
            <Controller
              name="phone" // Fixed: Changed from "mobile" to "phone" to match validation
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <PhoneInput
                  value={value}
                  onChange={(val, country) => {
                    const formattedNumber = formatPhoneNumber(val, country);
                    onChange(formattedNumber);
                  }}
                  placeholder="xxxx-xxxx xxxx"
                  defaultCountry="eg"
                  charAfterDialCode=""
                  className="w-full py-3 px-4 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:border-[#da0e1a] transition-colors text-base h-12"
                  inputClassName="w-full h-full"
                  inputStyle={{
                    background: "transparent",
                    border: "none",
                    color: "#000",
                    fontSize: "1rem",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                  countrySelectorStyleProps={{
                    buttonStyle: {
                      background: "transparent",
                      border: "none",
                      borderRight: "2px solid #e5e7eb",
                      padding: "0 1rem",
                      color: "#6b7280",
                      width: "4rem",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              )}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">
                {errors.phone.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="bg-gradient-to-r from-[#da0e1a] to-[#c40d17] rounded-lg w-full py-3 font-semibold text-xl cursor-pointer transition-colors hover:from-[#c40d17] hover:to-[#da0e1a] mb-2 text-white flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && (
              <svg
                className="animate-spin h-6 w-6 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            )}
            {loading ? "Sending..." : "Send Reset Instructions"}
          </button>
        </form>

        {/* Success Message */}
        {successMessage && (
          <div className="text-green-600 text-center mt-6 bg-green-50 p-4 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="text-red-600 text-center mt-6 bg-red-50 p-4 rounded-lg">
            {errorMessage}
          </div>
        )}

        <div className="flex items-center w-full max-w-md my-6">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="mx-4 text-gray-400 text-sm">or</span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>

        <div className="flex items-center justify-center gap-3 text-lg w-full">
          <span className="text-gray-600">Remembered your password?</span>
          <Link
            to="/signin"
            className="underline text-[#da0e1a] hover:text-[#c40d17] font-medium"
          >
            Back to Login
          </Link>
        </div>
      </div>

      <div id="recaptcha-container" ref={recaptchaRef}></div>
    </div>
  );
}

export default ForgetPassword;
